// Shared job controls for the Live Map: the job-colored pin that focuses the
// map (and the list) on a job, and the icon link that opens the job details in
// a new tab.
// Used by the jobs pane and by the truck and site popups so they behave the same.
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

import { replaceParamsInRoute } from '@isd/lib/helpers/functions';

import { LiveMapJob } from '@/components/LiveMap/liveMapModel';
import PinToggle from '@/components/common/PinToggle';

import { reactAppJobDetailsRoute, reactAppUrl } from '@/env';

// Color for jobs that aren't on the current schedule (no LiveMapJob to read from)
const UNKNOWN_JOB_COLOR = '#777777';

/**
 * URL of a job's details page in react-app.
 * @param {string} jobId
 * @returns {string}
 */
export function getJobDetailsUrl(jobId: string): string {
  return `${reactAppUrl}${replaceParamsInRoute(reactAppJobDetailsRoute, {
    jobId,
  })}`;
}

/**
 * Icon link that opens the job details page in a new tab.
 * @param {object} props
 * @param {string} props.jobId
 * @param {string} props.jobName - For the accessible name.
 * @param {string} [props.className]
 * @returns {React.JSX.Element}
 */
export function JobDetailsLink({
  jobId,
  jobName,
  className,
}: {
  jobId: string;
  jobName: string;
  className?: string;
}) {
  return (
    <a
      href={getJobDetailsUrl(jobId)}
      target="_blank"
      rel="noopener noreferrer"
      className={clsx(
        'text-accent hover:bg-base-200 inline-flex shrink-0 items-center justify-center rounded p-0.5',
        className
      )}
      title="Open job details in a new tab"
      aria-label={`Open ${jobName} details in a new tab`}
      onClick={(event) => event.stopPropagation()}
    >
      <ArrowTopRightOnSquareIcon className="size-4" aria-hidden="true" />
    </a>
  );
}

/**
 * One job inside a map popup: the pin and the name pin the job (same as in the
 * jobs pane), and the icon opens its details in a new tab.
 * @param {object} props
 * @param {string} props.jobId
 * @param {LiveMapJob | undefined} props.job - Undefined when the job isn't on the current schedule.
 * @param {string} [props.subtitle] - e.g. status and client.
 * @param {boolean} props.isPinned
 * @param {(jobId: string) => void} props.onTogglePin
 * @returns {React.JSX.Element}
 */
export function PopupJobRow({
  jobId,
  job,
  subtitle,
  isPinned,
  onTogglePin,
}: {
  jobId: string;
  job: LiveMapJob | undefined;
  subtitle?: string;
  isPinned: boolean;
  onTogglePin: (jobId: string) => void;
}) {
  const jobName = job?.name || 'Job not on this schedule';
  const canPin = !!job;
  return (
    <div
      className={clsx(
        'flex items-start gap-1.5 rounded-md px-1 py-1',
        isPinned && 'bg-base-200'
      )}
    >
      <PinToggle
        isPinned={isPinned}
        onToggle={() => onTogglePin(jobId)}
        label={jobName}
        color={job?.color || UNKNOWN_JOB_COLOR}
        disabled={!canPin}
      />
      <button
        type="button"
        className={clsx(
          'min-w-0 flex-1 text-left',
          canPin ? 'cursor-pointer' : 'cursor-default'
        )}
        onClick={canPin ? () => onTogglePin(jobId) : undefined}
        disabled={!canPin}
        title={
          !canPin
            ? undefined
            : isPinned
              ? 'Unpin this job'
              : 'Pin this job to focus the map and the list on it'
        }
      >
        <span className="line-clamp-2 font-semibold">{jobName}</span>
        {!!subtitle && (
          <span className="block text-xs opacity-70">{subtitle}</span>
        )}
      </button>
      <JobDetailsLink jobId={jobId} jobName={jobName} />
    </div>
  );
}
